import React, { useEffect, useMemo, useState } from "react";
import { Box, useTheme } from "@mui/material";
import { isAxiosError } from "axios";
import { useCompanyCongressionalTrades } from "./hooks/useCompanyCongressionalTrades";
import {
  CongressionalTrade,
  newestTradesFirst,
} from "./congressionalTradeData";
import { CongressionalTradeTable } from "./components/CongressionalTradeTable";
import {
  ChamberControl,
  DisclosurePagination,
  InvestorWorkspace,
  SectionTitle,
  WorkspaceHeader,
  WorkspaceState,
} from "./components/InvestorWorkspace";
import { roundedPanelSx } from "../Dashboard/overviewPanel";
import { useCompanyProfile } from "../Dashboard/Charts/hooks/useCompanyProfile";
import Logo from "../shared/Logo";
import { getSymbolImageUrl } from "../../lib/symbolImage";

interface CongressionalTradesByTickerProps {
  ticker: string;
}
const CongressionalTradesByTicker = ({
  ticker,
}: CongressionalTradesByTickerProps) => {
  const theme = useTheme();
  const symbol = ticker.trim().toUpperCase();
  const { data: profileData } = useCompanyProfile(symbol);
  const profile = Array.isArray(profileData) ? profileData[0] : profileData;
  const query = useCompanyCongressionalTrades(symbol);
  const profileName = profile?.companyName?.trim();
  const companyName = profileName ? profileName : symbol;
  const [chamber, setChamber] = useState<"all" | "house" | "senate">("all");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  useEffect(() => {
    setPage(0);
    setChamber("all");
  }, [ticker]);
  const trades = useMemo(() => {
    const combined: CongressionalTrade[] = [
      ...(query.data?.houseTrades ?? []).map((trade) => ({
        ...trade,
        chamber: "house" as const,
      })),
      ...(query.data?.senateTrades ?? []).map((trade) => ({
        ...trade,
        chamber: "senate" as const,
      })),
    ];
    return newestTradesFirst(
      combined.filter(
        (trade) => chamber === "all" || trade.chamber === chamber,
      ),
    );
  }, [query.data, chamber]);
  const visible = trades.slice(page * pageSize, (page + 1) * pageSize);
  const missing =
    isAxiosError(query.error) && query.error.response?.status === 404;
  return (
    <InvestorWorkspace tour="congressional-ticker-results">
      <WorkspaceHeader
        title={companyName}
        description={symbol}
        backTo="/super-investors"
        backLabel="Search stocks"
      >
        <Logo
          key={symbol}
          src={getSymbolImageUrl(symbol)}
          alt={`${companyName} logo`}
          ticker={symbol}
          companyName={companyName}
          size={32}
        />
      </WorkspaceHeader>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
        }}
      >
        <SectionTitle count={query.data && trades.length}>
          Trading activity
        </SectionTitle>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: { xs: "flex-start", sm: "flex-end" },
            flexWrap: "wrap",
            gap: 1.5,
            ml: { xs: 0, sm: "auto" },
          }}
        >
          <ChamberControl
            value={chamber}
            includeAll
            onChange={(next) => {
              setChamber(next);
              setPage(0);
            }}
          />
        </Box>
      </Box>
      {query.isLoading && <WorkspaceState loading />}
      {!query.isLoading && query.isError && !missing && (
        <WorkspaceState
          message="Unable to load congressional activity."
          retry={() => {
            void query.refetch();
          }}
        />
      )}
      {!query.isLoading && missing && (
        <WorkspaceState message="No trading disclosures available for this stock." />
      )}
      {!query.isLoading && !query.isError && (
        <Box
          component="section"
          aria-label="Disclosures"
          data-disclosure-panel
          sx={{
            ...roundedPanelSx(theme.palette.mode === "dark"),
            scrollMarginTop: "calc(80px + env(safe-area-inset-top, 0px))",
            "& .disclosure-label": {
              fontSize: "0.8125rem",
              color: "text.secondary",
              textTransform: "none",
              letterSpacing: 0,
              mb: 0.5,
            },
          }}
        >
          <CongressionalTradeTable trades={visible} />
          <DisclosurePagination
            page={page}
            pageSize={pageSize}
            count={visible.length}
            total={trades.length}
            hasNext={(page + 1) * pageSize < trades.length}
            onPage={setPage}
            onPageSize={(size) => {
              setPageSize(size);
              setPage(0);
            }}
          />
        </Box>
      )}
    </InvestorWorkspace>
  );
};
export default CongressionalTradesByTicker;
